import { z } from 'zod';

import { stripOfferMarkers } from '../offer-marker';
import type { FilterResolutionDigest } from '../turn-outcome-digest';
import { resolveGroundedOffer } from './artifact-grounding';
import {
  offerReportNoMatchResult,
  offerReportResult,
  offerReportSamePageUnresolvedReason,
  turnAbortedNoOpResult,
} from './tool-result-copy';
import type { ClientToolDefinition } from './types';

const schema = z
  .object({
    reportTypes: z.array(z.string()),
    prompt: z.string(),
    narration: z.string().optional(),
    pageOptionId: z.string().optional(),
    pageFilterArtifactId: z.string().optional(),
  })
  .passthrough();

export const offerReportTool: ClientToolDefinition<typeof schema> = {
  schema,
  // Terminal tool that APPENDS CHIPS (the report cards): the streamed
  // narration must render ABOVE them — explanation first, then the buttons —
  // so the loop flushes it as the answer BEFORE running the tool.
  narrationPolicy: 'flush-before-chip',
  async run(
    { reportTypes, prompt, narration, pageOptionId, pageFilterArtifactId },
    { artifacts, deps, signal }
  ) {
    const result = await deps.offerReport({ reportTypes, prompt });
    if (result.reportNames.length === 0) {
      return offerReportNoMatchResult;
    }
    // OPTIONAL page ride-along. Resolved BEFORE the narration renders so an
    // unresolvable page id downgrades its `offer:page` marker to plain text
    // instead of leaving a dead trigger with no backing chip — same ordering
    // rule `offer_page_or_custom` follows. withContinuation:false: clicking
    // the page is a terminal "land me there", and replaying this prompt on
    // the destination would re-answer it.
    const { grounded: pageGrounded, filtersUnresolved: pageFiltersUnresolved } =
      resolveGroundedOffer(artifacts, pageFilterArtifactId);
    // Ride-along onto the page already on screen: there is nothing to open, so
    // apply the page's filters here and let the report chips stand alone.
    const samePageRideAlong =
      pageOptionId !== undefined &&
      deps.getCurrentPageOptionId() === pageOptionId;
    let samePageFilterDigest: FilterResolutionDigest | undefined;
    let samePageFilterError: string | undefined;
    if (samePageRideAlong && pageGrounded !== undefined) {
      try {
        const digest = await deps.applyFilters({
          filterResponse: pageGrounded,
          target: 'current_page',
        });
        if (digest.applied.length > 0) {
          samePageFilterDigest = digest;
        } else {
          samePageFilterError = offerReportSamePageUnresolvedReason(digest);
        }
      } catch (error) {
        samePageFilterError =
          error instanceof Error ? error.message : 'unknown apply error';
      }
    }
    // Re-check after the LAST await: everything below WRITES (a nav_confirm
    // chip, the report offers, assistant text). An engine-unmount or
    // new-turn abort has no transcript rewind, so a stray write here leaves a
    // live clickable offer in a dead turn (see ClientToolContext.signal).
    if (signal?.aborted) {
      return turnAbortedNoOpResult;
    }
    // A page whose filters resolved nothing would open on its defaults, so
    // the report(s) stand alone.
    const pageResult =
      pageOptionId && !samePageRideAlong && !pageFiltersUnresolved
        ? deps.requestNavigateConfirm({
            optionId: pageOptionId,
            withContinuation: false,
            ...(pageGrounded !== undefined
              ? { resolvedFilters: pageGrounded }
              : {}),
          })
        : undefined;
    const pageOffered =
      pageResult !== undefined && !('unknownPage' in pageResult)
        ? pageResult
        : undefined;
    // Render the validated `narration` as the assistant text, stamped with
    // the offers message id so its [Report Name](offer:report) marker(s)
    // become inline triggers wired to the SAME open-report-builder action as
    // the cards (which the stamp then suppresses). Sibling pattern:
    // offer_standard_page.
    if (narration) {
      // An `offer:nav` marker with no resolved page chip must not survive as
      // a dead trigger — strip it back to plain text, exactly as
      // offer_page_or_custom does on an unknown page id.
      const text = pageOffered
        ? narration
        : stripOfferMarkers(narration, 'nav');
      deps.appendAssistantText(text, {
        ...(result.messageId ? { report: result.messageId } : {}),
        ...(pageOffered ? { nav: pageOffered.messageId } : {}),
      });
    }
    return offerReportResult({
      reportNames: result.reportNames,
      pageOfferedName: pageOffered?.pageName,
      // Truthiness, not `!== undefined`: an empty-string id is not a page
      // attempt, so it must not trigger the "did not resolve" note.
      pageTriedButUnresolved:
        Boolean(pageOptionId) &&
        pageOffered === undefined &&
        !samePageRideAlong &&
        !pageFiltersUnresolved,
      pageFiltersUnresolved,
      samePageRideAlong,
      samePageFilterDigest,
      samePageFilterError,
    });
  },
};
